/**
 * How journal writes behave without a server.
 *
 * Four policies, one per endpoint. Each names the entity (the entry id),
 * echoes its effect into the cached entry and, where the sidebar is affected,
 * the cached index, and says how revisions travel. Conflict handling lives in
 * journalConflicts.ts. Registered at import; `main.tsx` imports this module
 * at boot so a flush from any page finds the policies, and journalFileApi
 * imports it so they exist wherever the api does.
 */

import { registerWritePolicy } from '@shared/offline';

import { moveToSuggestedPath, offlineCopy, retryWithoutRevision } from './journalConflicts';
import {
  INDEX_KEY,
  entryKey,
  idFromUrl,
  indexRowFor,
  indexRowFromEntry,
  journalEntity,
  overlayFrontmatter,
  parseSimpleFrontmatter,
  sortIndexRows,
  splitFrontmatterBlock,
} from './journalContent';

import type { JournalIndexEntry } from './journalIndexApi';
import type { NotesFileContent } from '@features/notes/services/types';
import type { LocalStore, WritePolicy, WriteRequest } from '@shared/offline';

const ENTRY_URL = /^\/api\/journal\/files\/by-id\/[^/]+$/;
const FRONTMATTER_URL = /^\/api\/journal\/files\/by-id\/[^/]+\/frontmatter$/;
const CREATE_URL = '/api/journal/files';

type Frontmatter = Record<string, unknown>;
type CreateBody = { id: string; path: string; content: string };

/** The two columns a title/tag edit changes on the entry's sidebar row. */
type RowBaseline = Pick<JournalIndexEntry, 'title' | 'tags'>;

/**
 * The cached entry plus three client-only fields beside the server revision:
 * the pre-edit body while a body edit is queued, and the pre-edit frontmatter
 * and sidebar row while a title/tag edit is queued. The baselines let a
 * discard put back what the echo replaced without touching the other edit's
 * echo; the row is kept verbatim because it cannot be rebuilt from the
 * frontmatter alone (a create's row carries hashtags a PATCH never touches).
 */
type Cached = NotesFileContent & {
  baseline?: string;
  fmBaseline?: Frontmatter;
  rowBaseline?: RowBaseline;
};

const entityOf = (req: WriteRequest): string => journalEntity(idFromUrl(req.url));

const cachedEntry = (store: LocalStore, id: string): Promise<Cached | null> =>
  store.get<Cached>(entryKey(id));

/** Drop the cached entry so the next read refetches it; the undo for any echo that only edited it. */
const forgetEntry = async (store: LocalStore, req: WriteRequest): Promise<void> =>
  store.delete(entryKey(idFromUrl(req.url)));

const revisionOf = (reply: unknown): string | null =>
  (reply as { revision?: string | null } | undefined)?.revision ?? null;

const byteLength = (text: string): number => new TextEncoder().encode(text).length;

const frontmatterOf = (req: WriteRequest): Frontmatter =>
  (req.body as { frontmatter: Frontmatter }).frontmatter;

/**
 * Put the row in the index in the server's order: a row already there is
 * replaced in place, a new one goes after the rows of its date (the server
 * lists a date's entries oldest-created first).
 */
async function upsertIndexRow(store: LocalStore, row: JournalIndexEntry): Promise<void> {
  const rows = (await store.get<JournalIndexEntry[]>(INDEX_KEY)) ?? [];
  const present = rows.some(r => r.id === row.id);
  const next = present ? rows.map(r => (r.id === row.id ? row : r)) : [...rows, row];
  await store.put(INDEX_KEY, sortIndexRows(next));
}

/** The entry's sidebar row, if the index is cached and lists it. */
async function indexRowOf(store: LocalStore, id: string): Promise<JournalIndexEntry | null> {
  const rows = await store.get<JournalIndexEntry[]>(INDEX_KEY);
  return rows?.find(r => r.id === id) ?? null;
}

/** Take the row out of the cached index, if the index is cached at all. */
async function removeIndexRow(store: LocalStore, id: string): Promise<void> {
  const rows = await store.get<JournalIndexEntry[]>(INDEX_KEY);
  if (rows)
    await store.put(
      INDEX_KEY,
      rows.filter(r => r.id !== id)
    );
}

/** Add or replace `expected_revision` on a body; null removes it. */
function withExpected(b: unknown, revision: string | null): unknown {
  const { expected_revision: _dropped, ...rest } = (b ?? {}) as Record<string, unknown>;
  return revision ? { ...rest, expected_revision: revision } : rest;
}

/** Lay a frontmatter map over the entry's sidebar row the way the server's PATCH does, if the row is cached. */
async function mirrorFrontmatterToIndex(
  store: LocalStore,
  id: string,
  fm: Frontmatter
): Promise<void> {
  const row = await indexRowOf(store, id);
  if (row) await upsertIndexRow(store, overlayFrontmatter(row, fm));
}

/** Put the sidebar row's title and tags back as they were before the queued title/tag edit. */
async function restoreIndexRow(store: LocalStore, id: string, from: RowBaseline): Promise<void> {
  const row = await indexRowOf(store, id);
  if (row) await upsertIndexRow(store, { ...row, title: from.title, tags: from.tags });
}

/** The sidebar row to keep beside a queued title/tag edit: the one already kept, else the row as it is now. */
function stashedRow(cached: Cached, row: JournalIndexEntry | null): { rowBaseline?: RowBaseline } {
  const kept = cached.rowBaseline ?? (row ? { title: row.title, tags: row.tags } : undefined);
  return kept ? { rowBaseline: kept } : {};
}

/**
 * Echo a title/tag edit into the cached entry. A queued edit keeps the
 * pre-edit frontmatter and sidebar row beside it (the first ones, across
 * coalesced re-echoes) so a discard can put both back; an acknowledged edit
 * is the server's now and drops both baselines.
 */
async function echoFrontmatter(
  store: LocalStore,
  id: string,
  cached: Cached,
  fm: Frontmatter,
  reply: unknown
): Promise<void> {
  const { fmBaseline: _fm, rowBaseline: _row, ...rest } = cached;
  const baselines = reply
    ? {}
    : {
        fmBaseline: cached.fmBaseline ?? cached.frontmatter ?? {},
        ...stashedRow(cached, await indexRowOf(store, id)),
      };
  await store.put(entryKey(id), {
    ...rest,
    ...baselines,
    frontmatter: fm,
    title: String(fm.title ?? cached.title),
    revision: revisionOf(reply) ?? cached.revision ?? null,
  });
}

/** The revision adapter every by-id write shares: reply first, cached copy second. */
const entryRevision: WritePolicy['revision'] = {
  fromResponse: revisionOf,
  fromCache: async (store, req) => (await cachedEntry(store, idFromUrl(req.url)))?.revision ?? null,
  withExpected,
};

const body: WritePolicy = {
  replay: 'idempotent',
  entity: entityOf,
  coalesce: 'last-wins',
  describe: () => 'Journal · text',
  touches: req => [entryKey(idFromUrl(req.url))],
  echo: req => ({ id: idFromUrl(req.url), path: '', queued: true }),
  applyLocally: async (store, req, reply) => {
    const id = idFromUrl(req.url);
    const cached = await cachedEntry(store, id);
    if (!cached) return;
    const content = (req.body as { content: string }).content;
    const { baseline: _old, ...rest } = cached;
    // A queued edit keeps the body it replaced (the first one, across
    // coalesced re-echoes) so a discard can put it back; an acknowledged
    // edit is the server's body now and needs no baseline.
    const baseline = reply ? {} : { baseline: cached.baseline ?? cached.body };
    await store.put(entryKey(id), {
      ...rest,
      ...baseline,
      body: content,
      content,
      size: byteLength(content),
      revision: revisionOf(reply) ?? cached.revision ?? null,
    });
  },
  undoLocally: async (store, req) => {
    const id = idFromUrl(req.url);
    const cached = await cachedEntry(store, id);
    if (cached?.baseline === undefined) return store.delete(entryKey(id));
    const { baseline, ...rest } = cached;
    await store.put(entryKey(id), {
      ...rest,
      body: baseline,
      content: baseline,
      size: byteLength(baseline),
    });
  },
  revision: entryRevision,
  onConflict: offlineCopy,
};

const frontmatter: WritePolicy = {
  replay: 'idempotent',
  entity: entityOf,
  coalesce: 'last-wins',
  describe: () => 'Journal · title/tags',
  touches: req => [entryKey(idFromUrl(req.url)), INDEX_KEY],
  echo: req => {
    const fm = frontmatterOf(req);
    // No `revision` key at all: a hook reading `null` here would take it
    // for "forget the revision you hold", and the queued write moved nothing.
    return {
      id: idFromUrl(req.url),
      path: '',
      title: String(fm.title ?? ''),
      frontmatter: fm,
      queued: true,
    };
  },
  applyLocally: async (store, req, reply) => {
    const id = idFromUrl(req.url);
    const fm = frontmatterOf(req);
    const cached = await cachedEntry(store, id);
    if (cached) await echoFrontmatter(store, id, cached, fm, reply);
    await mirrorFrontmatterToIndex(store, id, fm);
  },
  undoLocally: async (store, req) => {
    const id = idFromUrl(req.url);
    const cached = await cachedEntry(store, id);
    if (!cached?.fmBaseline) return store.delete(entryKey(id));
    const { fmBaseline, rowBaseline, ...rest } = cached;
    await store.put(entryKey(id), {
      ...rest,
      frontmatter: fmBaseline,
      title: String(fmBaseline.title ?? rest.title),
    });
    // The row as it was beats one rebuilt from the frontmatter: a PATCH never
    // touches the hashtags a create put on it.
    if (rowBaseline) await restoreIndexRow(store, id, rowBaseline);
    else await mirrorFrontmatterToIndex(store, id, fmBaseline);
  },
  // Threads forward like a body write: the server keeps one revision per row
  // and a title change bumps it, so the next body save must carry the new one.
  // A conflict resolved by the no-revision retry threads nothing (the flusher
  // removes a resolved row without a reply), so a body save after it meets the
  // conflict it should.
  revision: entryRevision,
  onConflict: retryWithoutRevision,
};

/**
 * An idempotent create that had already landed replies with the entry as it
 * is now, edits from elsewhere included; that, not the request, is the truth.
 * The row is built from the server's parsed frontmatter (its date first,
 * as `_extract_date` looks), never re-parsed from the body.
 */
async function echoServerEntry(
  store: LocalStore,
  id: string,
  requestedPath: string,
  server: Partial<NotesFileContent> & { body: string }
): Promise<void> {
  const entry = { ...server, id, revision: server.revision ?? null } as Cached;
  await store.put(entryKey(id), entry);
  const path = entry.path ?? requestedPath;
  const row = indexRowFromEntry({
    id,
    path,
    frontmatter: entry.frontmatter ?? {},
    body: server.body,
  });
  await upsertIndexRow(store, { ...row, title: entry.title ?? row.title });
}

/** Echo the create as requested: the entry the server will build from this content, and its row. */
async function echoRequestedEntry(
  store: LocalStore,
  { id, path, content }: CreateBody,
  revision: string | null
): Promise<void> {
  const { block, body: text } = splitFrontmatterBlock(content);
  const row = indexRowFor({ id, path, content });
  await store.put(entryKey(id), {
    id,
    path,
    title: row.title,
    content,
    frontmatter: block ? parseSimpleFrontmatter(block) : {},
    body: text,
    size: byteLength(text),
    modified_at: '',
    revision,
  });
  await upsertIndexRow(store, row);
}

const create: WritePolicy = {
  replay: 'idempotent',
  entity: req => journalEntity((req.body as CreateBody).id),
  createsEntity: true,
  describe: req => `Journal · new entry · ${(req.body as CreateBody).path.replace(/\.md$/, '')}`,
  touches: req => [entryKey((req.body as CreateBody).id), INDEX_KEY],
  echo: req => ({
    id: (req.body as CreateBody).id,
    path: (req.body as CreateBody).path,
    queued: true,
  }),
  applyLocally: async (store, req, reply) => {
    const requested = req.body as CreateBody;
    const server = reply as (Partial<NotesFileContent> & { body?: unknown }) | undefined;
    // A create reply always carries the entry (`_entry_file_response`), so
    // this branch is the queued echo: no reply, no revision to take from it.
    if (typeof server?.body === 'string') {
      return echoServerEntry(
        store,
        requested.id,
        requested.path,
        server as typeof server & { body: string }
      );
    }
    const existing = await cachedEntry(store, requested.id);
    await echoRequestedEntry(store, requested, existing?.revision ?? null);
  },
  undoLocally: async (store, req) => {
    const { id } = req.body as CreateBody;
    await store.delete(entryKey(id));
    await removeIndexRow(store, id);
  },
  revision: { fromResponse: revisionOf, withExpected: b => b },
  onConflict: moveToSuggestedPath,
};

const remove: WritePolicy = {
  replay: 'idempotent',
  entity: entityOf,
  supersedes: true,
  describe: () => 'Journal · delete',
  touches: req => [entryKey(idFromUrl(req.url)), INDEX_KEY],
  echo: () => ({ message: 'queued' }),
  acceptStatus: status => status === 404,
  applyLocally: async (store, req) => {
    const id = idFromUrl(req.url);
    await store.delete(entryKey(id));
    await removeIndexRow(store, id);
  },
  // The removed row cannot be put back without its data; the refresh restores it.
  undoLocally: forgetEntry,
};

/** The four policies by name, for tests and for anything that needs to echo by hand. */
export const journalWritePolicies = { body, frontmatter, create, remove };

registerWritePolicy('POST', CREATE_URL, create);
registerWritePolicy('PUT', ENTRY_URL, body);
registerWritePolicy('PATCH', FRONTMATTER_URL, frontmatter);
registerWritePolicy('DELETE', ENTRY_URL, remove);
